import type { LscLiveReadinessReport } from '../types';

export type LiveReadinessDisplayState = 'live_detected' | 'ready' | 'warning' | 'blocked';

const SETUP_ISSUE_LABELS: Record<string, string> = {
  eventsub_subscriptions_incomplete: 'Setting up Twitch alerts for some members.',
  eventsub_online_missing: 'Setting up Twitch alerts for some members.',
  eventsub_offline_missing: 'Setting up Twitch alerts for some members.',
  eventsub_pending: 'Setting up Twitch alerts...',
  eventsub_platform_disabled: 'Live detection is not available on the platform right now.',
  members_missing_twitch: 'Some members are missing Twitch linkage.',
  missing_twitch: 'Some members are missing Twitch linkage.',
  missing_twitch_user_id: 'Some members are missing Twitch user IDs.',
  no_twitch_members: 'No league members are linked to Twitch yet.',
};

const BLOCKER_EXPLANATIONS: Record<string, string> = {
  no_active_stage: 'Set an active week/stage before running a live verification check.',
  no_active_season: 'Set an active season before running a live verification check.',
  no_open_matches: 'Import or refresh the schedule so open matches exist for live verification.',
  schedule_stale: 'Refresh the schedule — it may be out of date.',
  never_synced: 'Import the league schedule before running a live verification check.',
  eventsub_subscriptions_incomplete: 'Live detection is automatic once members connect Twitch.',
  no_test_candidates: 'No members are ready with an open match and Twitch linkage.',
  ...SETUP_ISSUE_LABELS,
};

export function resolveLiveReadinessDisplayState(
  readiness: LscLiveReadinessReport
): LiveReadinessDisplayState {
  if (readiness.live_state_status.qualified_live_games_count > 0) {
    return 'live_detected';
  }
  if (readiness.overall_status === 'ready') return 'ready';
  if (readiness.overall_status === 'warning') return 'warning';
  return 'blocked';
}

export function liveReadinessStatusHeading(displayState: LiveReadinessDisplayState): string {
  switch (displayState) {
    case 'live_detected':
      return 'Live game detected';
    case 'ready':
      return 'Ready for live verification';
    case 'warning':
      return 'Ready with warnings';
    case 'blocked':
      return 'Blocked for live verification';
  }
}

export function liveReadinessStatusBody(
  displayState: LiveReadinessDisplayState,
  readiness: LscLiveReadinessReport
): string {
  switch (displayState) {
    case 'live_detected': {
      const count = readiness.live_state_status.qualified_live_games_count;
      return count === 1
        ? '1 qualified league game is live now.'
        : `${count} qualified league games are live now.`;
    }
    case 'ready':
      return 'Live detection is automatic for connected members.';
    case 'warning':
      return 'Live verification can proceed, but some setup items may need attention.';
    case 'blocked':
      return explainTopBlocker(readiness.blockers[0]);
  }
}

export function explainTopBlocker(blocker?: string): string {
  if (!blocker) return 'Resolve league setup issues before running a live verification check.';
  return BLOCKER_EXPLANATIONS[blocker] || blocker.replace(/_/g, ' ');
}

export function formatSetupIssueLabel(code: string): string {
  return SETUP_ISSUE_LABELS[code] || code.replace(/_/g, ' ');
}

export function uniqueSetupIssueLabels(codes: string[]): string[] {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const code of codes) {
    const label = formatSetupIssueLabel(code);
    if (seen.has(label)) continue;
    seen.add(label);
    labels.push(label);
  }
  return labels;
}

export function informationalWarnings(warnings: string[]): string[] {
  return warnings.filter((warning) => warning !== 'live_idle');
}
